import { assign, defaultTo, each, has, isNil, isFunction, includes, findIndex, range, camelCase } from 'lodash';
import { Howler as SoundLibrary } from 'howler';
import { v4 as uuid } from 'uuid';
import WebFontLoader from 'webfontloader';
import { getLocaleFontInfo, getTranslation } from '@/translations';
import { getOption } from './options';
import { Assets, BitmapFont, TextMetrics, utils, VideoResource } from '../pixi';
import { LogType } from '../models';
import logger from './Logger';
import { getCustomAssetsForGame } from './CustomAssets';

const eventPrefix = 'Nsft.';
const loaderContext = 'CasinoLoaderProgress';
let gameAssetsBaseUrl;

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function getIOSVersion() {
  const ua = window.navigator.userAgent;
  // Match iOS version from the user agent
  const match = ua.match(/OS (\d+)_?(\d+)?_?(\d+)?/);
  if (/iPad|iPhone|iPod/.test(ua) && match) {
    const major = parseInt(match[1], 10);
    const minor = parseInt(match[2], 10);
    const patch = parseInt(match[3] || '0', 10);
    return { major, minor, patch };
  }

  return null; // Not iOS
}

/**
 Fix for wrong font metrics causing text to be cut off, caused by canvas farbling.
 This override is a backport of the method used in Pixi v8 to calculate font metrics more accurately.
 Task: CU-869eta4wq
 GitHub: https://github.com/pixijs/pixijs/issues/11369
*/
export function useBoundingBoxFontMetrics() {
  TextMetrics.measureFont = (font) => {
    const { _fonts: cache, _context: context } = TextMetrics;

    // As this method is used for preparing assets, don't recalculate things if we don't need to
    if (cache[font]) {
      return cache[font];
    }

    context.font = font;

    const metrics = context.measureText(TextMetrics.METRICS_STRING + TextMetrics.BASELINE_SYMBOL);
    const ascent = metrics.actualBoundingBoxAscent ?? 0;
    const descent = metrics.actualBoundingBoxDescent ?? 0;

    cache[font] = {
      ascent,
      descent,
      fontSize: ascent + descent,
    };

    return cache[font];
  };
}

// Loads Roboto (+ optional locale font) from Google Fonts with a timeout and rasterizes BitmapFont textures.
// If the locale defines specialChars, they are warmed via document.fonts.load before rasterization.
// If the locale defines googleFamily (e.g. Noto Sans Georgian), it is loaded and prepended to the Canvas font stack
// so non-Latin glyphs render with matching weights instead of a system fallback.
export async function loadFonts(fonts = []) { // eslint-disable-line default-param-last
  try {
    const defaultFontFamily = 'Roboto';
    const defaultFontWeights = [300, 500, 700, 900];
    const fontSize = 54;
    const localeFontInfo = getLocaleFontInfo();
    const specialChars = localeFontInfo?.specialChars;
    const localeGoogleFamily = localeFontInfo?.googleFamily;
    const fontNames = fonts.map((font) => font.name);

    const fontFamiliesWithWeights = fontNames.map((name) => (
      name.includes(':') ? name : `${name}:${defaultFontWeights.join()}`
    ));

    // Locale font goes first so its glyphs are matched by weight before Roboto is tried for Latin.
    const bitmapFontFamily = [
      localeGoogleFamily,
      defaultFontFamily,
      'Arial',
      'sans-serif',
    ].filter(Boolean).join(', ');

    const bitmapChars = [
      ...BitmapFont.ALPHANUMERIC,
      '–,.+-:()!/|’"%\'=&#', // Special characters
      '$€£₦', // Currency symbols
      ...(specialChars ? [specialChars] : []),
    ];

    const googleFamilies = [defaultFontFamily, localeGoogleFamily]
      .filter(Boolean)
      .map((family) => `${family}:${defaultFontWeights.join()}`);

    await new Promise((resolve) => {
      WebFontLoader.load({
        google: {
          families: [...googleFamilies, ...fontFamiliesWithWeights],
        },
        timeout: 10000,
        active: resolve,
        inactive: resolve,
      });
    });

    // Ensure the exact (family, weight, size) tuples are ready in document.fonts before rasterization.
    if (specialChars && document.fonts?.load) {
      const preloadFamily = localeGoogleFamily || defaultFontFamily;
      const pending = [
        ...defaultFontWeights.map((weight) => (
          document.fonts.load(`${weight} ${fontSize}px "${preloadFamily}"`, specialChars)
        )),
        ...fontNames.flatMap((name) => {
          const family = name.split(':')[0];
          return defaultFontWeights.map((weight) => (
            document.fonts.load(`${weight} ${fontSize}px "${family}"`, specialChars)
          ));
        }),
      ];
      await Promise.all(pending).catch(() => { /* fall back to Canvas font stack */ });
    }

    const lineHeight = Math.floor(fontSize * 1.08);
    const bitmapVariants = [
      { suffix: 'Light', weight: 300 },
      { suffix: 'Normal', weight: 500 },
      { suffix: 'SemiBold', weight: 700 },
      { suffix: 'Bold', weight: 900 },
    ];

    bitmapVariants.forEach(({ suffix, weight }) => {
      BitmapFont.from(`${defaultFontFamily}${suffix}`, {
        fontFamily: bitmapFontFamily,
        fontSize,
        fontWeight: weight,
        fill: 'white',
        lineHeight,
      }, {
        chars: bitmapChars,
      });
    });
  } catch (error) {
    logger.log(LogType.Warn, 'FontLoadError', error.message);
  }
}

export async function loadAssets(state, customAssetsBaseUrl, customAssets, fonts, manualEnd = false, locale = 'en', landscapeMode = false) {
  let gameCustomAssets = customAssets;
  gameAssetsBaseUrl = defaultTo(customAssetsBaseUrl, 'https://storage.googleapis.com/nsoftcasino');
  const gameCustomAssetsBaseUrl = `${gameAssetsBaseUrl}/resources/slots`;
  const gameAssets = state ? defaultTo(state.game.assets, {}) : {};
  const gameAssetKeys = [];
  const loader = Assets;
  const resources = {};

  /*
  Load fonts.
  */
  await loadFonts(fonts, locale);

  /*
  Add common slot assets.
  */
  if (state?.game?.name) {
    if (isNil(gameCustomAssets)) {
      gameCustomAssets = {};
    }

    const slotCustomAssets = getCustomAssetsForGame(state.game.name);

    each(slotCustomAssets, (name) => {
      gameCustomAssets[name.split('.')[0]] = {
        path: `${gameCustomAssetsBaseUrl}/${name}`,
      };
    });
  }

  if (gameCustomAssets) {
    assign(gameAssets, gameCustomAssets);
  }

  loader.reset();
  SoundLibrary.unload();

  const assetsToSkip = [
    landscapeMode ? 'portrait' : 'landscape',
    '_static',
  ];

  each(gameAssets, async (value, key) => {
    const skipAsset = assetsToSkip.some((s) => key.toLowerCase().includes(s));

    if (skipAsset) return;

    const data = value;

    if (value.path.endsWith('mp4')) {
      Object.assign(data, {
        resourceOptions: {
          updateFPS: 25,
          autoPlay: true,
          muted: true,
        },
      });
    }

    gameAssetKeys.push(key);

    loader.add({
      alias: key,
      src: value.path,
      data,
    });
  });

  let loadedCount = 0;
  const loadedCountTotal = gameAssetKeys.length;
  const loadedErrorKeys = [];
  const promises = [];

  function setLoaderProgress() {
    const progressPercentage = Math.ceil((loadedCount / loadedCountTotal) * 100);
    const setProgress = !manualEnd || progressPercentage !== 100;

    // If manualEnd is true progress will go up to 99%. 100% will be set inside finishLoading()
    if (setProgress) {
      window[loaderContext] = progressPercentage;

      if (window.integration?.trackLoaderProgress) {
        window.integration.setLoaderProgress({ progressPercentage });
      }
    }
  }

  // eslint-disable-next-line
  for (const gameAssetKey of gameAssetKeys) {
    const promise = new Promise((resolve, reject) => { // eslint-disable-line no-loop-func
      loader.load(gameAssetKey).then((loadedResource) => {
        resources[gameAssetKey] = {
          name: gameAssetKey,
          path: gameAssets[gameAssetKey].path,
          mimeType: gameAssets[gameAssetKey].mimeType,
          resource: loadedResource,
        };

        loadedCount += 1;
        setLoaderProgress();
        resolve();
      }, (error) => {
        logger.log(LogType.Warn, 'AssetLoadError', error.message);
        loadedErrorKeys.push(gameAssetKey);
        reject();
      });
    });

    promises.push(promise);
  }

  await Promise.allSettled(promises);

  /*
  Retry failed assets one more time and if it failes
  continue with just logging error.
  */
  if (loadedErrorKeys.length > 0) {
    // eslint-disable-next-line
    for await (const gameAssetKey of loadedErrorKeys) {
      try {
        const loadedResource = await loader.load(gameAssetKey);

        resources[gameAssetKey] = {
          name: gameAssetKey,
          path: gameAssets[gameAssetKey].path,
          mimeType: gameAssets[gameAssetKey].mimeType,
          resource: loadedResource,
        };

        loadedCount += 1;
        setLoaderProgress();
      } catch (error) {
        logger.log(LogType.Warn, 'AssetLoadRepeatError', error.message);
      }
    }
  }

  return resources;
}

export async function loadCustomAssets(assets) {
  return loadAssets(undefined, undefined, assets);
}

export function registerEventListener(name, event, source, once = false) {
  const sourceName = defaultTo(source, 'Default');
  const uniqueName = `${eventPrefix}${name}`;
  const uniqueHandlerName = `${uniqueName}${sourceName}`;

  if (window[uniqueHandlerName]) {
    document.removeEventListener(uniqueName, window[uniqueHandlerName]);
  }

  window[uniqueHandlerName] = event;
  document.addEventListener(uniqueName, window[uniqueHandlerName], { once });
}

export function triggerEvent(name, detail) {
  const uniqueName = `${eventPrefix}${name}`;

  document.dispatchEvent(new CustomEvent(uniqueName, {
    detail,
  }));
}

export function removeEventListener(name, source) {
  const sourceName = defaultTo(source, 'Default');
  const uniqueName = `${eventPrefix}${name}`;
  const uniqueHandlerName = `${uniqueName}${sourceName}`;

  if (window[uniqueHandlerName]) {
    document.removeEventListener(uniqueName, window[uniqueHandlerName]);
  }
}

export function triggerAnalyticsEvent(detail) {
  const uniqueName = `${eventPrefix}AnalyticsAdd`;

  document.dispatchEvent(new CustomEvent(uniqueName, {
    detail,
  }));
}

export function finishLoading() {
  window[loaderContext] = 100;

  const revealDurationMs = 1400;

  triggerEvent('LoadingFinished', { revealDurationMs });

  setTimeout(() => {
    triggerEvent('GameRevealed');
  }, revealDurationMs);
}

export function getAspectRatio() {
  return {
    aspectRatio: window.innerHeight / window.innerWidth,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

export function getUuid() {
  return uuid();
}

export function getParticleColorBehavior(colors) {
  let colorBehavior;
  const defaultColors = ['#ebeb17', '#e771f7'];

  if (colors.length === 1 || (colors.length === 2 && colors[0] === colors[1])) {
    colorBehavior = {
      type: 'colorStatic',
      config: {
        color: defaultTo(colors[0], defaultColors[0]),
      },
    };
  } else {
    colorBehavior = {
      type: 'color',
      config: {
        color: {
          list: [
            {
              value: defaultTo(colors[0], defaultColors[0]),
              time: 0,
            },
            {
              value: defaultTo(colors[1], defaultColors[1]),
              time: 1,
            },
          ],
        },
      },
    };
  }

  return colorBehavior;
}

export function fixMultiSprite(allAssets) {
  each(allAssets, (asset) => {
    const { resource } = asset;
    if (resource && resource.data && resource.data.meta) {
      /*
      Normal property in texturer packer is related_multi_pack,
      but we are changing in json files to related since original
      name breaks pixi loader as it does not merge textures as it
      should, but leaves undefined textures from related pack.
      */
      const relatedMultiPacks = resource.data.meta.related;
      if (relatedMultiPacks) {
        let multiPackTextureMap = { ...resource.textures };
        if (relatedMultiPacks.length > 0) {
          each(relatedMultiPacks, (relatedMultiPack) => {
            const relatedAsset = relatedMultiPack.replace('.json', '').replace('-0', '');
            if (allAssets[relatedAsset]) {
              const relatedMultiPackSheet = allAssets[relatedAsset].resource;
              if (relatedMultiPackSheet.textures) {
                multiPackTextureMap = assign(
                  multiPackTextureMap,
                  relatedMultiPackSheet.textures,
                );

                resource.textures = multiPackTextureMap;
              }
            }
          });
        }

        each(resource.data.animations, (animation, animationKey) => {
          const resourceAnimations = resource.animations;
          resourceAnimations[animationKey] = animation.map(
            (frame) => multiPackTextureMap[frame],
          );
        });
      }
    }
  });
}

export const getRequestFullscreenMethod = () => document.body.requestFullscreen || document.body.webkitRequestFullscreen;

export const isTapForFullscreenAvailable = () => {
  const isValidMobile = utils.isMobile.any && !utils.isMobile.other.firefox && !utils.isMobile.apple.phone;
  const isValidMethod = isFunction(getRequestFullscreenMethod());
  const isFullscreenModeAvailable = document.fullscreenEnabled || document.webkitFullscreenEnabled || document.mozFullScreenEnabled || document.msFullscreenEnabled;
  const isFullscreenSet = document.fullscreenElement === document.body;

  return isValidMobile && isFullscreenModeAvailable && isValidMethod && !isFullscreenSet && !window.integration?.topBarHeight;
};

export const setFullscreen = () => {
  if (isTapForFullscreenAvailable()) {
    const method = getRequestFullscreenMethod();
    method.bind(document.body)();
    triggerEvent('FullscreenSet');
  }
};

export const getAssetsUrl = () => gameAssetsBaseUrl;

export function getLocalStorage() {
  return {
    isSupported() {
      const test = 'localStorage';

      try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    },
    getItem(key) {
      if (this.isSupported) {
        return localStorage.getItem(key);
      }

      return null;
    },
    removeItem(key) {
      if (this.isSupported) {
        localStorage.removeItem(key);
        return this.getItem(key);
      }

      return null;
    },
    setItem(key, value) {
      if (this.isSupported) {
        localStorage.setItem(key, value);
        return this.getItem(key);
      }

      return null;
    },
  };
}

export function getTextureIndexByChar(textures, char) {
  return findIndex(textures, (texture) => texture.textureCacheIds[0].indexOf(`_${char}.`) > -1);
}

export function isSpriteVideo(sprite) {
  return sprite.texture.baseTexture.resource instanceof VideoResource;
}

export async function callApiWithRetries(method, props = [], retries = 3, wait = 300, error = undefined) {
  if (!retries) {
    return error;
  }

  const result = await method(...props);

  if (result.isError) {
    await sleep(wait);
    return callApiWithRetries(method, props, retries - 1, 300, result);
  }

  return result;
}

export function getPaylineCenter(payline, columnsLength, rowsLength) {
  const paylineWindow = [];

  range(columnsLength).forEach((_, reelIndex) => {
    paylineWindow.push([]);

    range(rowsLength).forEach((symbolIndex) => {
      if (isNil(payline[reelIndex][symbolIndex])) {
        paylineWindow[reelIndex].push(undefined);
      } else {
        paylineWindow[reelIndex].push(payline[reelIndex][symbolIndex]);
      }
    });
  });

  const centerRow = Math.floor((paylineWindow.length - 1) / 2);
  const centerCol = Math.floor((paylineWindow[0].length - 1) / 2);
  let center = null;
  let centerDist = Infinity;

  for (let i = 0; i < paylineWindow.length; i++) {
    for (let j = 0; j < paylineWindow[0].length; j++) {
      if (typeof paylineWindow[i][j] === 'number') {
        const dist = Math.abs(i - centerRow) + Math.abs(j - centerCol);
        if (dist < centerDist) {
          center = [i, paylineWindow[i][j]];
          centerDist = dist;
        }
      }
    }
  }

  return center;
}

export function getErrorParams(errorDetails) {
  /* WriteConflict check - used until backend resolve write confilct problem, task: https://app.clickup.com/t/861m867kj */
  const errorMessage = errorDetails?.ui?.label || errorDetails?.message;
  const errorSnackbarType = errorDetails?.ui?.action.toLowerCase() === 'crash' ? 'error' : 'info';
  const errorTranslationKey = includes(errorMessage, 'WriteConflict') ? 'writeConflict' : camelCase(errorMessage);
  const isErrorClosable = errorSnackbarType === 'info';
  const isErrorContinuable = errorDetails?.ui?.action.toLowerCase() === 'continue';
  const isErrorUiDefined = has(errorDetails, 'ui') && !isNil(errorDetails?.ui);
  const isRciError = errorDetails?.ui?.display && errorDetails.message.toLowerCase() === 'rci_error';

  if (isRciError) {
  /* trigger RCI error in client slots */
    triggerEvent('ShowRciError', { ...errorDetails, isRciError });
  }

  const notification = {
    text: defaultTo(getTranslation(errorTranslationKey), errorMessage),
    type: errorSnackbarType,
    isClosable: isErrorClosable,
    isReloadable: !isErrorUiDefined,
    errorMessage,
  };

  const errorParams = {
    isErrorClosable,
    isErrorContinuable,
    isErrorReloadable: !isErrorUiDefined,
    isRciError,
    notification,
  };

  return errorParams;
}

export function setErrorDetails({ details, boost, openNotification }) {
  if (window.integration?.shouldIntegrationHandleError({ response: details })) {
    window.integration.getErrorObjectAndHandleError({ response: details });
  } else if (details) {
    const errorDetails = getErrorParams(details);

    // Hide/disable boost
    if (boost) {
      if (errorDetails.isErrorReloadable) {
        boost.hide();
      } else {
        boost.disable();
      }
    }

    if (!errorDetails.isRciError) {
      openNotification(errorDetails.notification);
    }

    return errorDetails;
  }

  return undefined;
}

export function getDomain() {
  return process.env.NODE_ENV === 'development' ? '7platform.net' : window.location.origin.split('.').slice(-2).join('.');
}

export function useStandardUrl() {
  return ['7platform.net', 'nsoft.app', 'netlify.app', 'pages.dev'].includes(getDomain());
}

export function openPublicRoundPreview(roundId) {
  window.open(`${process.env.APP_ROUND_PREVIEW_NEW_URL}/rounds/${roundId}`, '_blank');
}

export function getRootOrParentHost() {
  try {
    const origin = window.location.ancestorOrigins?.[0];
    if (origin) {
      return new URL(origin).host;
    }

    if (document.referrer) {
      return new URL(document.referrer).host;
    }

    return window.location.host || null;
  } catch {
    return null;
  }
}

export function roundNumberDecimalPoints(number, decimalPoints = 2) {
  return parseFloat(number.toFixed(decimalPoints));
}

export function hasGameVariant(gameName, ...variants) {
  const name = String(gameName).trim().toLowerCase();
  const variantsToCheck = variants.length ? variants : ['90'];
  return variantsToCheck.some((variant) => name.endsWith(String(variant).trim().toLowerCase()));
}

export function isWithinBonusBuyLimit(price) {
  const limits = getOption('bonusBuyDisabledOnLimit');
  const currency = getOption('currency');

  if (limits?.[currency]) {
    return price <= limits[currency];
  }

  return true;
}
